import { FieldProps } from 'formik'
import { has, isArray, noop } from 'lodash-es'
import { ITag, ITagPickerProps, Label, TagPicker } from 'office-ui-fabric-react'
import * as React from 'react'
import { isObject } from 'util'

export type FormikTagPickerProps = FieldProps<any> & {
  tags: ITag[]
  label?: string
  required?: boolean
  defaultSelectedItem?: ITag
  suggestionLimit?: number
  suggestionsHeaderText?: string
  itemLimit?: number
  /**
   * Callback that receives the last added item
   */
  onChange?: (item: ITag) => void
  /**
   * Callback that receives the current list of selected items
   * after they changed
   */
  onItemsChanged?: (items: ITag[] | undefined) => void
  /**
   * Same as onChange, for interchangeability with FormikDropdown
   */
  onChanged?: (item: ITag) => void
} & Partial<
    Omit<
      ITagPickerProps,
      'onItemSelected' | 'onChange' | 'defaultSelectedItems' | 'itemLimit'
    >
  >

export type TagPickerCustomProps = Omit<FormikTagPickerProps, 'field' | 'form'>

export class FormikTagPicker extends React.Component<FormikTagPickerProps> {
  private tags: ITag[] = []
  private defaultItems: ITag[] | undefined
  constructor(props: FormikTagPickerProps) {
    super(props)
    this._createOptions(props)
    this._setDefaultItems(props)
    this.state = { selectedItems: this.defaultItems }
  }

  public componentWillReceiveProps(nextProps: FormikTagPickerProps) {
    if (nextProps.field.value === null) {
      this.setState({ selectedItems: [] })
    }
    this._createOptions(nextProps)
  }

  public render(): JSX.Element {
    return (
      <>
        {this.props.label && (
          <Label required={this.props.required}>{this.props.label}</Label>
        )}

        <TagPicker
          {...this.props}
          selectedItems={(this.state as any).selectedItems}
          onChange={(items) => {
            this.setState({ selectedItems: items })
            if (this.props.onItemsChanged) {
              this.props.onItemsChanged(items)
            }

            if (!items || items.length === 0) {
              this.props.form.setFieldValue(this.props.field.name, undefined)
            }
          }} // disable normal onChange
          pickerSuggestionsProps={{
            suggestionsHeaderText: this.props.suggestionsHeaderText,
          }}
          itemLimit={this.props.itemLimit || 1}
          onEmptyInputFocus={this._onEmptyInputFocus(
            this.tags,
            this.props.suggestionLimit
          )}
          onResolveSuggestions={
            this.props.onResolveSuggestions ||
            this._onFilterChanged(this.props.suggestionLimit)
          }
          defaultSelectedItems={this.defaultItems}
          onItemSelected={(item) => {
            if (item) {
              if (this.props.onChange) {
                this.props.onChange(item)
              }
              if (this.props.onChanged) {
                this.props.onChanged(item)
              }
              this.props.form.setFieldValue(this.props.field.name, item.key)
              return item
            }

            return null
          }}
        />
      </>
    )
  }

  private _createOptions(props: FormikTagPickerProps) {
    this.tags = props.tags
  }

  private _setDefaultItems(props: FormikTagPickerProps) {
    if (props.defaultSelectedItem) {
      this.defaultItems = [props.defaultSelectedItem]
    }
    if (isArray(props.field.value)) {
      this.defaultItems = this.tags.filter((item) =>
        props.field.value.filter((key: string) => item.key === key)
      )
    } else if (isObject(props.field.value) && has(props.field.value, '_id')) {
      const defaultItem = this.tags.find((i) => i.key === props.field.value._id)
      this.defaultItems = defaultItem ? [defaultItem] : undefined
    } else {
      const defaultItem = this.tags.find((i) => i.key === props.field.value)
      this.defaultItems = defaultItem ? [defaultItem] : undefined
    }
  }

  private _onFilterChanged(limit: number | undefined) {
    return (filterText: string) => {
      let filteredTags = this.tags
      if (filterText) {
        filteredTags = this.tags.filter(
          (tag) =>
            `${tag.name.toLowerCase()}${tag.key.toLowerCase()}`.indexOf(
              filterText.toLowerCase()
            ) !== -1
        )
      }
      return filteredTags.slice(0, limit || 500)
    }
  }

  private _onEmptyInputFocus(tags: ITag[], limit: number | undefined) {
    if (limit) {
      return () => tags.slice(0, limit)
    }
    return () => tags
  }
}

// This is the above picker without formik stuff, that can easily replace dropdowns
export class TagPickerCustom extends React.Component<TagPickerCustomProps> {
  private tags: ITag[] = []
  private defaultItems: ITag[] | undefined

  constructor(props: FormikTagPickerProps) {
    super(props)
    this._createOptions(props)
  }

  public componentWillReceiveProps(nextProps: FormikTagPickerProps) {
    this._createOptions(nextProps)
  }

  public render(): JSX.Element {
    return (
      <>
        {this.props.label && (
          <Label required={this.props.required}>{this.props.label}</Label>
        )}
        <TagPicker
          {...this.props}
          onChange={(_) => {
            noop()
          }} // disable normal onChange
          pickerSuggestionsProps={{
            suggestionsHeaderText: this.props.suggestionsHeaderText,
          }}
          itemLimit={this.props.itemLimit || 1}
          onEmptyInputFocus={this._onEmptyInputFocus(
            this.tags,
            this.props.suggestionLimit
          )}
          onResolveSuggestions={this._onFilterChanged(
            this.props.suggestionLimit
          )}
          defaultSelectedItems={this.defaultItems}
          onItemSelected={(item) => {
            if (item) {
              if (this.props.onChange) {
                this.props.onChange(item)
              }
              if (this.props.onChanged) {
                this.props.onChanged(item)
              }
              return item
            }
            return null
          }}
        />
      </>
    )
  }

  private _createOptions(props: FormikTagPickerProps) {
    this.tags = props.tags
  }

  private _onFilterChanged(limit: number | undefined) {
    return (filterText: string) =>
      filterText
        ? this.tags.filter(
            (tag) =>
              tag.name.toLowerCase().indexOf(filterText.toLowerCase()) !== -1
          )
        : this.tags.slice(0, limit)
  }

  private _onEmptyInputFocus(tags: ITag[], limit: number | undefined) {
    if (limit) {
      return () => tags.slice(0, limit)
    }
    return () => tags
  }
}
